import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parse, stringify } from 'querystring';
import { FormattedMessage } from 'react-intl';
import { ChevronRight, Calendar } from 'react-feather';
import { path, propOr } from 'ramda';
import PropTypes from 'prop-types';
import moment from 'moment';

import Modal from 'components/ui/Modal';
import { Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import AlertBox from 'components/ui/AlertBox';
import FareIndicator from 'components/ui/FareIndicator';
import ApolloErrorSection from 'components/errors/ApolloErrorSection';
import StyledSelect from 'components/ui/StyledReactSelect';
import JourneySearchHeader from 'components/shared/JourneySearchHeader';
import JourneySearchTables from 'components/shared/JourneySearchTables';
import JourneyDigest from 'components/shared/JourneyDigest';

import { productFamilyCodes } from 'types/productFamilyCodes';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { bookingShape, historyShape, searchParamsShape, locationShape, stationsShape, clientShape, userShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import { removeEmptyValues, formatProductFamilyName } from 'utils/functions';
import { getOfflineFees } from 'utils/exchange';
import SearchRequestBookingMagnet from 'components/shared/SearchRequestBookingMagnet';
import { THALYS_OFFICES } from 'types/constants';

import { ADD_ADDITIONAL_DETAILS, UPDATE_EXTRAS_GET_BOOKING } from 'store/mutations';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import {
  buildSegments,
  buildOutboundJourneys,
  buildInboundJourneys,
  buildLegsFromFare,
  hasProductFamilyMismatch,
  getTotalPrice,
  buildInboundInfo,
  calcSegmentPointsTotal,
  buildMultiJourneyAdditionalDetails
} from './JourneySearch.utils';

import {
  StyledHorizontalRule,
  StyledPage,
  ConfirmButton,
  StyledToggleButton,
  Grid,
  AvailablePointsCell,
  AvailablePointsBoldCell,
  ClubEurostarImg,
  Error,
  HereLink,
  StyledFareTypeContainer
} from './JourneySearch.styles';
import { GET_CUSTOMER_DETAILS, GET_PROFILE } from '../../store/queries';
import { isLoyaltyTier3OrAbove } from 'utils/loyaltyData';
import { searchTypes } from 'pages/BookingMagnet/BookingMagnet.constants';
import groupProductFamilies from 'constants/groupProductFamilies';
import { getInfantCountFromSearch } from 'utils/getInfantPassengersFromDetails';

const initialState = {
  outbound: null,
  inbound: null,
  showBookingMagnet: false,
  isMember: false,
  activePoints: 0,
  showNotEnoughPoints: false,
  addingJouneyLoading: false,
  hasSpecialLoyaltyTier: false,
  selectedProductFamily: undefined,
  uniqProductFamiliesWithFares: []
};

const JourneySearch = (props) => {
  const {
    client,
    membershipNumber,
    searchParams,
    history: {
      push,
      location: { search, pathname, hash, state }
    },
    data,
    error,
    loading,
    stations,
    booking,
    user,
    cheapestFares
  } = props;

  const [journeySearchState, setJourneySearchState] = useState(initialState);

  const {
    outbound,
    inbound,
    showBookingMagnet,
    isMember,
    activePoints,
    showNotEnoughPoints,
    addingJouneyLoading,
    selectedProductFamily,
    uniqProductFamiliesWithFares
  } = journeySearchState;

  const setState = useCallback((nextState) => {
    setJourneySearchState((prevState) => ({
      ...prevState,
      ...(typeof nextState === 'function' ? nextState(prevState) : nextState)
    }));
  }, []);

  const errorParentRef = useRef({
    props: { history: { location: { pathname, search, hash }, push } },
    setState: () => {}
  });

  useEffect(() => {
    errorParentRef.current = {
      props: {
        history: {
          push,
          location: {
            pathname,
            search,
            hash,
            state
          }
        }
      },
      setState: (parentState) => {
        setState(parentState);
      }
    };
  }, [pathname, push, search, hash, state, setState]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!membershipNumber) {
        return;
      }

      const result = await client.query({
        query: GET_CUSTOMER_DETAILS,
        variables: {
          membershipNumber
        }
      });

      const customerInfo = result?.data?.getCustomerDetails;
      if (customerInfo?.profile?.loyalty) {
        setState({
          isMember: true,
          activePoints: customerInfo.profile.loyalty?.rewardPoints,
          hasSpecialLoyaltyTier: isLoyaltyTier3OrAbove(customerInfo.profile?.loyalty?.tier.tierCode ?? '')
        });

        const subscriptionStartDate = new URLSearchParams(search).get('subscriptionStartDate');
        const subscriptionEndDate = new URLSearchParams(search).get('subscriptionEndDate');

        if (subscriptionStartDate && subscriptionEndDate) {
          setState({
            subscriptionStartDate,
            subscriptionEndDate
          });
        } else {
          const subscriptionID = new URLSearchParams(search).get('subscriptionID');

          if (subscriptionID) {
            try {
              const profileResult = await client.query({
                query: GET_PROFILE,
                variables: {
                  customerId: customerInfo?.profile?.customerId,
                  subscriptionSearchType: 'CURRENT'
                }
              });

              const subscription = profileResult?.data?.profile.subscriptions.find((s) => s.travelPassReference === subscriptionID);
              if (subscription) {
                setState({
                  subscriptionStartDate: subscription?.startDate,
                  subscriptionEndDate: subscription?.endDate
                });
              }
            } catch (err) {
              setState({
                subscriptionStartDate: undefined,
                subscriptionEndDate: undefined
              });
            }
          }
        }
      }
    };

    fetchCustomerDetails();
  }, [client, membershipNumber, search, setState]);

  const prevDataRef = useRef();
  useEffect(() => {
    if (prevDataRef.current === data) {
      return;
    }
    prevDataRef.current = data;

    const isNewSearchMode = searchParams.productFamilies.length === 0;

    if (isNewSearchMode) {
      const defaultProductFamily =
        searchParams.bookingTypeSelected === searchTypes.GROUP ? productFamilyCodes.GROUP : productFamilyCodes.PUB;

      const { outbound: searchOutbound, inbound: searchInbound } = data.journeySearch;
      const groupFamilies = new Set(groupProductFamilies);

      const uniqProductFamiliesWithFares = Array.from(
        new Set(
          [...(searchOutbound?.productFamiliesWithFares ?? []), ...(searchInbound?.productFamiliesWithFares ?? [])].filter(
            (productFamily) =>
              searchParams.bookingTypeSelected === searchTypes.GROUP ? groupFamilies.has(productFamily) : !groupFamilies.has(productFamily)
          )
        )
      );

      setState({
        uniqProductFamiliesWithFares,
        selectedProductFamily:
          uniqProductFamiliesWithFares.find((productFamily) => productFamily === defaultProductFamily) || uniqProductFamiliesWithFares[0]
      });
    }
  }, [data, searchParams, setState]);

  useEffect(() => {
    const { outboundDate, inboundDate } = parse(search.substr(1));

    const currentOutboundDate = outbound && outbound.date;
    const currentInboundDate = inbound && inbound.date;

    if (
      (!!outboundDate && !!currentOutboundDate && outboundDate !== currentOutboundDate) ||
      (!!inboundDate && !!currentInboundDate && inboundDate !== currentInboundDate)
    ) {
      setState(initialState);
      return;
    }

    if (moment(outboundDate).isAfter(inbound?.legs[0]?.timing?.departureDate)) {
      setState({ inbound: null });
    }
  }, [search, outbound, inbound, setState]);

  const isReturnJourney = !!path(['searchParams', 'inboundDate'], { searchParams });
  const { bookingTypeSelected } = searchParams;

  const offlineFee = useCallback(() => {
    const isThalysUser = user?.offices?.filter((office) => THALYS_OFFICES.includes(office.name))?.length > 0;

    return getOfflineFees({
      outbound,
      inbound,
      bookingTypeSelected,
      isThalysUser,
      hasSpecialLoyaltyTier: journeySearchState.hasSpecialLoyaltyTier,
      isPackageBooking: state?.isPackageBooking
    });
  }, [bookingTypeSelected, outbound, inbound, user, state, journeySearchState.hasSpecialLoyaltyTier]);

  const toggleSearchMenu = useCallback(() => {
    setState((prev) => ({
      showBookingMagnet: !prev.showBookingMagnet
    }));
  }, [setState]);

  const toggleNotEnoughPointsModal = useCallback(() => {
    setState((prev) => ({
      showNotEnoughPoints: !prev.showNotEnoughPoints
    }));
  }, [setState]);

  const selectFare = useCallback(
    (fareObj) => {
      const { direction } = fareObj;

      setState({
        [direction]: fareObj
      });
    },
    [setState]
  );

  const hasEnoughPoints = useCallback(() => {
    const outboundTotalPoints = calcSegmentPointsTotal(outbound);
    const inboundTotalPoints = calcSegmentPointsTotal(inbound);

    if (isMember && searchParams.productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY)) {
      if ((outboundTotalPoints || 0) + (inboundTotalPoints || 0) > activePoints) {
        setState({
          showNotEnoughPoints: true
        });

        return false;
      }
    }

    return true;
  }, [activePoints, isMember, outbound, inbound, searchParams, setState]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!hasEnoughPoints()) {
        return;
      }

      const segments = buildSegments({ outbound, inbound }, isReturnJourney);

      const fee = offlineFee();
      if (fee) {
        segments.push({
          startValidityDate: moment().format('YYYY-MM-DD'),
          items: [
            {
              passengerId: fee.passengerId,
              tariffCode: fee.tariffCode,
              price: fee.price
            }
          ]
        });
      }

      try {
        setState({ addingJouneyLoading: true });
        const {
          data: { updateBookingExtras: newBooking }
        } = await client.mutate({
          mutation: UPDATE_EXTRAS_GET_BOOKING,
          variables: {
            bookingReference: booking.reference,
            currency: booking.currency,
            segments
          }
        });

        const additionalDetails = buildMultiJourneyAdditionalDetails(booking, newBooking);

        await client.mutate({
          mutation: ADD_ADDITIONAL_DETAILS,
          variables: {
            bookingReference: booking.reference,
            additionalDetails
          }
        });

        if (user && user.isStation && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
          await client.mutate({
            mutation: ADD_ADDITIONAL_DETAILS,
            variables: {
              bookingReference: booking.reference,
              additionalDetails: [
                {
                  key: `tablet-create-${moment().format('HH:mm:ss.DDMMYY')}`,
                  value: user.paymentLocation && user.paymentLocation.name
                }
              ]
            }
          });
        }

        setState({ addingJouneyLoading: false });
        push(`/booking/${booking.reference}/review-booking`);
      } catch (submitError) {
        setState({ addingJouneyLoading: false });
        throw new AgentPortalError(submitError);
      }
    },
    [booking, client, hasEnoughPoints, isReturnJourney, offlineFee, outbound, inbound, push, setState, user]
  );

  const handleSearchChange = useCallback(
    (searchData) => {
      setState({ showBookingMagnet: false });

      const { travelDate, returnDate } = searchData;
      const existingQuery = parse(search.substr(1));
      const outboundDate = existingQuery.outboundDate && travelDate;
      const inboundDate = (outboundDate && returnDate) || (existingQuery.inboundDate && travelDate);

      const searchParams = removeEmptyValues({
        ...existingQuery,
        outboundDate,
        inboundDate
      });

      push({
        pathname: `/journeys`,
        search: stringify(searchParams),
        state: state
      });
    },
    [push, search, setState, state]
  );

  const handleSetProductFamily = useCallback(
    ({ value }) => {
      setState({
        outbound: null,
        inbound: null,
        selectedProductFamily: value
      });
    },
    [setState]
  );

  const {
    totalPrice = 0,
    outboundTotalPrice = 0,
    inboundTotalPrice = 0
  } = getTotalPrice({
    outbound: outbound || {},
    inbound: inbound || {},
    fees: [offlineFee()?.price || 0]
  });

  const { adultsWheelchair, childrenWheelchair, wheelchairCompanions, outboundDate, inboundDate, productFamilies, currency } = searchParams;

  const isLoyaltyBooking = productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY);
  const isLoyaltyLoggedOut = isLoyaltyBooking && !membershipNumber;
  const isUnassignedSearch = productFamilies.includes(productFamilyCodes.RED_US);

  const isWheelchairSearch = adultsWheelchair > 0 || childrenWheelchair > 0 || wheelchairCompanions > 0;

  const isSubmitDisabled = useCallback(() => {
    const isReturn = !!inboundDate;
    if (isReturn) {
      return !outbound || !inbound || isLoyaltyLoggedOut;
    }

    return !outbound || isLoyaltyLoggedOut;
  }, [inbound, inboundDate, isLoyaltyLoggedOut, outbound]);

  const searchWithoutQuestionMark = search.substr(1);
  const { originStationName, destinationStationName, returningOriginStationName, returningDestinationStationName } =
    parse(searchWithoutQuestionMark);

  const outboundInfo = {
    date: outboundDate,
    origin: originStationName,
    destination: destinationStationName,
    originUIC: searchParams.origin,
    destinationUIC: searchParams.destination
  };

  const inboundInfo = buildInboundInfo({
    date: inboundDate,
    returningOriginStationName,
    returningDestinationStationName,
    destinationStationName,
    originStationName,
    originUIC: searchParams.origin,
    destinationUIC: searchParams.destination,
    returningOriginUIC: searchParams.returningOrigin,
    returningDestinationUIC: searchParams.returningDestination
  });

  const searchMenuTextId = showBookingMagnet ? 'exchange.hideBookingMagnet' : 'exchange.showBookingMagnet';

  if (loading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    return (
      <>
        <JourneySearchHeader outbound={outboundInfo} inbound={inboundInfo} />
        <StyledHorizontalRule />
        <Row>
          <ApolloErrorSection error={new AgentPortalError(error, ACTION_CODES.BACK_TO_BOOKING_MAGNET)} parent={errorParentRef.current} />
        </Row>
      </>
    );
  }

  const { passengers, productFamiliesWithFares } = data.journeySearch;
  const isGroupBooking = (productFamilies?.length > 0 ? productFamilies : productFamiliesWithFares).includes(productFamilyCodes.GROUP);
  const getBookingItems = propOr([], 'bookingItems');
  const hasJourneys = getBookingItems(outbound).length > 0 || getBookingItems(inbound).length > 0;
  const hasMismatch = hasProductFamilyMismatch(productFamilies, productFamiliesWithFares);

  const uniqueFamilyGroupNames = [...new Set(productFamiliesWithFares.map((pf) => pf.familyGroupName))];
  const allProductFamiliesMessage = uniqueFamilyGroupNames.map((name) => formatProductFamilyName(name)).join(', ');

  const outboundTotalPoints = calcSegmentPointsTotal(outbound);
  const inboundTotalPoints = calcSegmentPointsTotal(inbound);

  const loyaltyError = isLoyaltyLoggedOut ? (
    <Error id="error-not-loyalty-account">
      <FormattedMessage
        id="errorBoundary.notLoggedInLoyalty"
        values={{
          here: (
            <HereLink
              onClick={() =>
                push('/clubeurostar/manage', {
                  prevPath: pathname,
                  prevSearch: search,
                  prevHash: hash
                })
              }
            >
              <FormattedMessage id="errorBoundary.linkHere" />
            </HereLink>
          )
        }}
      />
    </Error>
  ) : null;

  const fareOptions = uniqProductFamiliesWithFares.map((productFamily) => ({
    value: productFamily,
    label: productFamily
  }));

  if (data.journeySearch?.outbound?.journeys) {
    data.journeySearch.outbound.journeys = data.journeySearch.outbound.journeys.filter((journey) =>
      journey.fares?.some((fare) => fare.legs.some((leg) => leg.serviceType?.code === serviceTypeCodes.GREENSPEED))
    );
  }

  if (data.journeySearch?.inbound?.journeys) {
    data.journeySearch.inbound.journeys = data.journeySearch.inbound.journeys.filter((journey) =>
      journey.fares?.some((fare) => fare.legs.some((leg) => leg.serviceType?.code === serviceTypeCodes.GREENSPEED))
    );
  }

  return (
    <StyledPage columns={12}>
      <Row columns={12}>
        <Column span={3} start={14} justifyContent="flex-end">
          <StyledToggleButton onClick={toggleSearchMenu} id="toggle-search">
            <Calendar />
            <FormattedMessage id={searchMenuTextId} />
          </StyledToggleButton>
        </Column>
        {selectedProductFamily && (
          <Column span={3} start={14} justifyContent="flex-end">
            <StyledFareTypeContainer>
              <FormattedMessage id="bookingMagnet.fareType" tagName="span" />
              <StyledSelect
                id="outbound-fare-select"
                name="outbound-fare-select"
                data-testid="outbound-fare-select"
                data-cy="outbound-fare-select"
                className="react-select-container"
                classNamePrefix="react-select"
                width="200px"
                height="30px"
                options={fareOptions}
                value={{
                  value: selectedProductFamily,
                  label: selectedProductFamily
                }}
                onChange={handleSetProductFamily}
              />
            </StyledFareTypeContainer>
          </Column>
        )}
      </Row>

      <Row columns={12}>
        <AlertBox variant={AlertBox.variants.WARNING} dataCy="warning-multi-journeys-er-only">
          <FormattedMessage id="journeySearch.multiJourneyWarningER" />
        </AlertBox>
      </Row>

      {hasMismatch && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING} dataCy="warning-multi-journeys-mismatch-products-family">
            <FormattedMessage
              id="journeySearch.multipleProductFamilies"
              values={{
                actualProductFamilies: allProductFamiliesMessage,
                indicator: <FareIndicator data-cy="fare-indicator" />
              }}
            />
          </AlertBox>
        </Row>
      )}

      <Modal isOpen={showBookingMagnet} onClose={toggleSearchMenu}>
        <SearchRequestBookingMagnet
          origin={outboundInfo.originUIC}
          destination={outboundInfo.destinationUIC}
          returningOrigin={inboundInfo && inboundInfo.originUIC}
          returningDestination={inboundInfo && inboundInfo.destinationUIC}
          stations={stations}
          travelDates={{ outboundDate, inboundDate }}
          onSubmit={handleSearchChange}
          subscriptionStartDate={journeySearchState.subscriptionStartDate}
          subscriptionEndDate={journeySearchState.subscriptionEndDate}
          isExchange={false}
        />
      </Modal>

      <Modal isOpen={showNotEnoughPoints} onClose={toggleNotEnoughPointsModal}>
        <FormattedMessage id="journeySummary.notEnoughPoints" />
      </Modal>

      {isMember && productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY) && (
        <Row span={12}>
          <Grid>
            <AvailablePointsCell>
              <ClubEurostarImg />
            </AvailablePointsCell>
            <AvailablePointsCell>
              <FormattedMessage id="clubEurostar.activePoints" />
            </AvailablePointsCell>
            <AvailablePointsBoldCell>{activePoints}</AvailablePointsBoldCell>
          </Grid>
        </Row>
      )}

      <Row span={12}>
        <JourneySearchHeader
          outbound={outboundInfo}
          inbound={inboundInfo}
          toggleSearchMenu={toggleSearchMenu}
          cheapestFares={cheapestFares}
          currency={currency}
          subscriptionStartDate={journeySearchState.subscriptionStartDate}
          subscriptionEndDate={journeySearchState.subscriptionEndDate}
        />
      </Row>

      <Row>
        <JourneySearchTables
          shouldUseSelection
          outboundDate={outboundDate}
          inboundDate={inboundDate}
          isGroupBooking={isGroupBooking}
          outboundInfo={outboundInfo}
          inboundInfo={inboundInfo}
          isUnassignedSearch={isUnassignedSearch}
          outboundJourneys={buildOutboundJourneys({
            data,
            outboundDate,
            inboundDate,
            selectedInbound: inbound
          })}
          inboundJourneys={buildInboundJourneys({
            data,
            outboundDate,
            inboundDate,
            selectedOutbound: outbound
          })}
          selectFare={selectFare}
          selectedOutbound={outbound}
          selectedInbound={inbound}
          currency={currency}
          isWheelchairSearch={isWheelchairSearch}
          requestedProductFamilies={productFamilies}
          passengers={passengers}
          outboundClassesOfService={data.journeySearch.outbound?.classesOfService}
          inboundClassesOfService={data.journeySearch.inbound?.classesOfService}
          selectedProductFamily={selectedProductFamily}
        />
      </Row>

      {hasJourneys && (
        <>
          <JourneyDigest
            autoExpand
            outbound={buildLegsFromFare(outbound)}
            outboundTotalPrice={outboundTotalPrice}
            outboundTotalPoints={outboundTotalPoints}
            inbound={buildLegsFromFare(inbound)}
            inboundTotalPrice={inboundTotalPrice}
            inboundTotalPoints={inboundTotalPoints}
            totalPrice={totalPrice}
            isReturnJourney={isReturnJourney}
            passengers={passengers}
            currency={currency}
            offlineFee={offlineFee()}
            infantCount={getInfantCountFromSearch(search)}
            isLoyaltyBooking={isLoyaltyBooking}
            confirmButton={
              <ConfirmButton
                loading={loading || addingJouneyLoading}
                disabled={isSubmitDisabled() || addingJouneyLoading}
                type="submit"
                onClick={handleSubmit}
                data-cy="create-provisional"
                variant="primary"
              >
                <FormattedMessage id="journeySummary.reserverSeats" />
                <ChevronRight />
              </ConfirmButton>
            }
            confirmError={loyaltyError}
            originUIC={searchParams.origin}
            destinationUIC={searchParams.destination}
          />
        </>
      )}
    </StyledPage>
  );
};

JourneySearch.propTypes = {
  booking: bookingShape,
  history: historyShape,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  loading: PropTypes.bool,
  searchParams: searchParamsShape,
  location: locationShape,
  stations: stationsShape,
  client: clientShape,
  membershipNumber: PropTypes.string,
  cheapestFares: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      price: PropTypes.number
    })
  ),
  user: userShape
};

export default JourneySearch;
